// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { $debug } from '../../Utils/ConsoleUtils'
import { WebsocketErrorMessage, WebsocketMessage } from './Types/Types'

export type MockMessage<
    InboundMessage extends WebsocketMessage<unknown> | WebsocketErrorMessage,
    OutboundMessage extends WebsocketMessage<unknown>
> = {
    messageToServer?: OutboundMessage
    messageFromServer: InboundMessage
    delay?: number
}

// Complete mock class of window.WebSocket which implements WebSocket
export class MockWebSocket<
    InboundMessage extends WebsocketMessage<unknown> | WebsocketErrorMessage,
    OutboundMessage extends WebsocketMessage<unknown>
> implements WebSocket
{
    public readonly CLOSED = 3
    public readonly CLOSING = 2
    public readonly CONNECTING = 0
    public readonly OPEN = 1

    public readonly extensions: string = ''
    public readonly protocol: string = ''
    public readonly url: string = ''
    public readonly bufferedAmount: number = 0
    public readonly binaryType: BinaryType = 'blob'

    public onclose: OnlyNullable<EventListener> = null
    public onerror: OnlyNullable<EventListener> = null
    public onmessage: OnlyNullable<EventListener> = null
    public onopen: OnlyNullable<EventListener> = null
    public readyState: number = this.CONNECTING

    private mockMessages: MockMessage<WebsocketMessage<unknown> | WebsocketErrorMessage, WebsocketMessage<unknown>>[] =
        []
    private listeners: Map<string, EventListenerOrEventListenerObject[]> = new Map()

    constructor() {
        $debug('MockWebSocket created')
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public close(code?: number, reason?: string): void {
        $debug('MockWebSocket closed')
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public send(outboundMessage: string | ArrayBufferLike | Blob | ArrayBufferView): void {
        $debug('MockWebSocket::Outbound message sent', outboundMessage)

        const mockMessage: Nullable<
            MockMessage<WebsocketMessage<unknown> | WebsocketErrorMessage, WebsocketMessage<unknown>>
        > = this.mockMessages.find(
            (
                mMessage: MockMessage<WebsocketMessage<unknown> | WebsocketErrorMessage, WebsocketMessage<unknown>>
            ): boolean => JSON.stringify(mMessage.messageToServer) === outboundMessage
        )

        if (mockMessage) {
            this.listeners.get('message')?.forEach((listener: EventListenerOrEventListenerObject): void => {
                $debug('MockWebSocket::Inbound message sent', mockMessage.messageFromServer)

                setTimeout((): void => {
                    if (typeof listener === 'function') {
                        listener({ data: JSON.stringify(mockMessage.messageFromServer) } as MessageEvent<string>)
                    } else {
                        listener.handleEvent({
                            data: JSON.stringify(mockMessage.messageFromServer)
                        } as MessageEvent<string>)
                    }
                }, mockMessage.delay ?? 0)
            })
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public addEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
        $debug(`MockWebSocket added event listener for ${type}`)

        if (!this.listeners.has(type)) {
            this.listeners.set(type, [listener])
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public removeEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
        $debug(`MockWebSocket removed event listener for ${type}`)
    }

    public dispatchEvent(event: Event): boolean {
        $debug(`MockWebSocket dispatched event ${event.type}`)

        return true
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public attachEvent(event: string, listener: EventListener): boolean {
        $debug(`MockWebSocket attached event ${event}`)

        return true
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public detachEvent(event: string, listener: EventListener): void {
        $debug(`MockWebSocket detached event ${event}`)
    }

    public addMockMessage(mockMessage: MockMessage<InboundMessage | WebsocketErrorMessage, OutboundMessage>): void {
        if (mockMessage.messageToServer) {
            this.mockMessages.push(mockMessage)
        } else {
            setTimeout((): void => {
                this.listeners.get('message')?.forEach((listener: EventListenerOrEventListenerObject): void => {
                    if (typeof listener === 'function') {
                        listener({ data: JSON.stringify(mockMessage.messageFromServer) } as MessageEvent<string>)
                    } else {
                        listener.handleEvent({
                            data: JSON.stringify(mockMessage.messageFromServer)
                        } as MessageEvent<string>)
                    }
                })
            }, mockMessage.delay ?? 0)
        }

        this.readyState = this.OPEN
        this.listeners.get('open')?.forEach((listener: EventListenerOrEventListenerObject): void => {
            if (typeof listener === 'function') {
                listener({} as Event)
            } else {
                listener.handleEvent({} as Event)
            }
        })
    }
}
